"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { User, Mail, Lock, Phone, MapPin, Loader2, ArrowRight } from "lucide-react";
import { API_BASE_URL } from "@/lib/constants";

const LIME = "#ccfc45";

const fields = [
    { name: "firstName", label: "First Name", type: "text", icon: User, placeholder: "Henry" },
    { name: "lastName", label: "Last Name", type: "text", icon: User, placeholder: "Ford" },
    { name: "email", label: "Email Address", type: "email", icon: Mail, placeholder: "henry@example.com" },
    { name: "password", label: "Password", type: "password", icon: Lock, placeholder: "••••••••" },
    { name: "phone", label: "Phone Number", type: "tel", icon: Phone, placeholder: "+91 98765 43210" },
    { name: "address", label: "Business Address", type: "text", icon: MapPin, placeholder: "Mumbai, India" },
];

export default function SignupPage() {
    const router = useRouter();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [formData, setFormData] = useState({
        firstName: "", lastName: "", email: "", password: "", phone: "", address: "",
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const res = await fetch(`${API_BASE_URL}/api/auth/signup`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            const data = await res.json();
            if (res.ok) {
                router.push("/login");
            } else {
                setError(data.message || "Registration failed. Please check your details.");
            }
        } catch {
            setError("Something went wrong. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-black flex overflow-hidden">
            {/* Left brand panel */}
            <div
                className="hidden lg:flex flex-col justify-between p-14 relative overflow-hidden"
                style={{ width: "380px", background: "#0d0d0d", flexShrink: 0 }}
            >
                {/* Glow */}
                <div
                    className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full blur-[120px] opacity-20"
                    style={{ background: LIME }}
                />

                <Link href="/" className="flex items-center gap-3 z-10">
                    <div className="relative w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center">
                        <Image
                            src="/brand-icon.png"
                            alt="Restaubee Logo"
                            width={40}
                            height={40}
                            className="object-cover"
                        />
                    </div>
                    <span className="font-black text-2xl tracking-tighter text-white">
                        Restau<span style={{ color: LIME }}>bee</span>
                    </span>
                </Link>

                <div className="z-10">
                    <motion.p
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                        className="text-4xl font-black tracking-tighter text-white leading-tight mb-5"
                    >
                        Grow your<br />
                        <span style={{ color: LIME }}>restaurant.</span>
                    </motion.p>
                    <p className="text-gray-500 text-sm leading-relaxed">
                        One account. Unlimited outlets. Completely free, forever.
                    </p>

                    <div className="flex flex-col gap-4 mt-8">
                        {["Free forever", "No credit card needed", "5-minute setup"].map((t, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.4 + i * 0.1 }}
                                className="flex items-center gap-3 text-sm font-bold text-gray-400"
                            >
                                <span className="text-base" style={{ color: LIME }}>✓</span>
                                {t}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right: form */}
            <div className="flex-1 flex items-center justify-center px-6 py-12 overflow-y-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="w-full max-w-xl"
                >
                    {/* Mobile logo */}
                    <div className="lg:hidden flex items-center gap-2 mb-8">
                        <div className="relative w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center">
                            <Image
                                src="/brand-icon.png"
                                alt="Restaubee Logo"
                                width={32}
                                height={32}
                                className="object-cover"
                            />
                        </div>
                        <span className="font-black text-xl tracking-tighter text-white">
                            Restau<span style={{ color: LIME }}>bee</span>
                        </span>
                    </div>

                    <h1 className="text-4xl font-black tracking-tighter text-white mb-2">
                        Create account.
                    </h1>
                    <p className="text-gray-500 mb-10 font-medium">
                        Set up your free POS admin account.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-bold"
                            >
                                {error}
                            </motion.div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {fields.map(({ name, label, type, icon: Icon, placeholder }) => (
                                <div key={name} className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-wider text-gray-500">
                                        {label}
                                    </label>
                                    <div className="relative group">
                                        <Icon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 group-focus-within:text-white transition-colors" size={16} />
                                        <input
                                            type={type}
                                            name={name}
                                            required
                                            placeholder={placeholder}
                                            value={(formData as any)[name]}
                                            onChange={handleChange}
                                            className="w-full h-12 bg-white/5 border border-white/8 rounded-xl pl-11 pr-4 text-white font-bold text-sm focus:outline-none focus:border-white/20 focus:bg-white/8 transition-all placeholder:text-gray-700"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <motion.button
                            type="submit"
                            disabled={loading}
                            whileTap={{ scale: 0.97 }}
                            className="w-full h-14 rounded-xl font-black text-black text-sm flex items-center justify-center gap-3 mt-4 transition-opacity disabled:opacity-50"
                            style={{ background: LIME }}
                        >
                            {loading
                                ? <Loader2 className="animate-spin" size={20} />
                                : <><span>Create Free Account</span><ArrowRight size={18} /></>
                            }
                        </motion.button>
                    </form>

                    <p className="text-center mt-8 text-gray-600 text-sm font-medium">
                        Already have an account?{" "}
                        <Link href="/login" className="text-white font-black hover:opacity-80 transition-opacity">
                            Sign in →
                        </Link>
                    </p>
                    <p className="text-center mt-4 text-gray-700 text-xs">
                        By joining you agree to our{" "}
                        <span className="text-gray-500 underline cursor-pointer hover:text-white transition-colors">
                            Terms of Service
                        </span>
                    </p>
                </motion.div>
            </div>
        </div>
    );
}
